import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const GuestBookForm = () => {
  const [currentLanguage, setCurrentLanguage] = useState('ar');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    isOver18: false,
    isUnder18: false,
    acceptedTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const timeoutRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const formContent = {
    ar: {
      title: " احضر يوم الذكرى الأولى للتحرير",
      description: "سجّل حضورك الآن عبر ملء النموذج المخصّص.",
      nameLabel: "الاسم الكامل",
      namePlaceholder: "أدخل إسمك الكامل كما هو مسجل في الوثيقة القانونية التركية",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل بريدك الإلكتروني",
      locationLabel: "المدينة",
      locationPlaceholder: "أدخل مدينتك",
      countryLabel: "البلد",
      countryPlaceholder: "اختر بلدك",
      messageLabel: "رسالة التذكر",
      messagePlaceholder: "شارك رسالتك أو ذكرياتك...",
      submitButton: "سجل",
      submittingButton: "جاري الإرسال...",
      successTitle: "شكراً لك!",
      successMessage: "تم إضافة توقيعك بنجاح إلى دفتر الضيوف. رسالتك ستكون جزءاً من التاريخ.",
      okButton: "حسناً، فهمت",
      signatureCount: "توقيع",
      requiredError: "هذا الحقل مطلوب",
      emailError: "يرجى إدخال بريد إلكتروني صحيح",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخل رقم هاتفك",
      phoneError: "يرجى إدخال رقم هاتف صحيح",
      phoneLimitError: "تم استخدام هذا الرقم 3 مرات بالفعل. الحد الأقصى المسموح به هو 3 إرسالات.",
      submitError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.",
      numberOfAttendantsLabel: "عدد المرافقين",
      numberOfAttendantsPlaceholder: "كم عدد الأشخاص الذين سيرافقونك؟",
      isFamilyMemberLabel: "هل أنت من عائلة شهيد أو معتقل أو مُغَيَّب؟",
      familyMemberTypeLabel: "ما نوع الحالة؟",
      familyMemberTypePlaceholder: "اختر نوع الحالة",
      fatherBranchLabel: "ما كان فرع والدك؟",
      fatherBranchPlaceholder: "اختر فرع والدك",
      martyrTimingLabel: "هل كان ذلك قبل معركة رد العدوان أم بعدها؟",
      martyrTimingPlaceholder: "اختر التوقيت",
      isOver18Label: "أؤكد أنني أبلغ من العمر 18 عاماً أو أكثر",
      isUnder18Label: "أؤكد أنني أقل من 18 عاماً",
      ageGroupError: "يجب تحديد فئتك العمرية",
      acceptedTermsLabel: "أؤكد على صحة المعلومات التي أدخلتها وأوافق على معالجتها ومشاركتها للغرض التي طلبت لأجله فقط ضمن قانون حماية البيانات الشخصية KVKK",
      acceptedTermsError: "يجب الموافقة على الشروط والأحكام",
      arabicNameError: "لا يمكن إدخال الأحرف العربية. يرجى استخدام الأحرف الإنجليزية أو التركية فقط",
      formClosedMessage: "نعتذر لإغلاق فورم التسجيل بسبب إقبال عدد كبير يفوق سعة القاعة"
    },
    en: {
      title: "Sign the Guest Book",
      description: "Leave a message of remembrance and be part of history",
      nameLabel: "Full Name",
      namePlaceholder: "Enter your full name as registered in the Turkish legal document",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email address",
      locationLabel: "City",
      locationPlaceholder: "Enter your city",
      countryLabel: "Country",
      countryPlaceholder: "Select your country",
      messageLabel: "Message of Remembrance",
      messagePlaceholder: "Share your message or memories...",
      submitButton: "Register",
      submittingButton: "Submitting...",
      successTitle: "Thank You!",
      successMessage: "Your signature has been successfully added to the guest book. Your message will be part of history.",
      okButton: "Ok, I got it",
      signatureCount: "signatures",
      requiredError: "This field is required",
      emailError: "Please enter a valid email address",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      phoneError: "Please enter a valid phone number",
      phoneLimitError: "This phone number has already been used 3 times. Maximum allowed is 3 submissions.",
      submitError: "Failed to submit. Please try again.",
      numberOfAttendantsLabel: "Number of Attendants",
      numberOfAttendantsPlaceholder: "How many are you bringing with you?",
      isFamilyMemberLabel: "Are you a family member of a martyr, detained, or absent person?",
      familyMemberTypeLabel: "What is the status?",
      familyMemberTypePlaceholder: "Select the status",
      fatherBranchLabel: "What was your father's branch?",
      fatherBranchPlaceholder: "Select your father's branch",
      martyrTimingLabel: "If he was a martyr, was it before or after the battle of Rad' al-'udwān?",
      martyrTimingPlaceholder: "Select timing",
      isOver18Label: "I confirm that I am 18 years of age or older",
      isUnder18Label: "I confirm that I am under 18 years of age",
      ageGroupError: "You must select your age group",
      acceptedTermsLabel: "I confirm the accuracy of the information entered and agree to its processing and sharing solely for the requested purpose within the Personal Data Protection Law (KVKK)",
      acceptedTermsError: "You must accept the terms and conditions",
      arabicNameError: "Arabic characters are not allowed. Please use only English or Turkish letters",
      formClosedMessage: "We apologize for closing the registration form due to a large turnout that exceeds the capacity of the hall"
    },
    tr: {
      title: "Ziyaretçi Defterini İmzala",
      description: "Anma mesajınızı bırakın ve tarihin bir parçası olun",
      nameLabel: "Ad Soyad",
      namePlaceholder: "Tam adınızı Türk yasal belgesinde kayıtlı olduğu şekilde girin",
      emailLabel: "E-posta Adresi",
      emailPlaceholder: "E-posta adresinizi girin",
      locationLabel: "Şehir",
      locationPlaceholder: "Şehrinizi girin",
      countryLabel: "Ülke",
      countryPlaceholder: "Ülkenizi seçin",
      messageLabel: "Anma Mesajı",
      messagePlaceholder: "Mesajınızı veya anılarınızı paylaşın...",
      submitButton: "Kaydol",
      submittingButton: "Gönderiliyor...",
      successTitle: "Teşekkürler!",
      successMessage: "İmzanız başarıyla ziyaretçi defterine eklendi. Mesajınız tarihin bir parçası olacak.",
      okButton: "Tamam, Anladım",
      signatureCount: "imza",
      requiredError: "Bu alan gereklidir",
      emailError: "Lütfen geçerli bir e-posta adresi girin",
      phoneLabel: "Telefon Numarası",
      phonePlaceholder: "Telefon numaranızı girin",
      phoneError: "Lütfen geçerli bir telefon numarası girin",
      phoneLimitError: "Bu telefon numarası zaten 3 kez kullanıldı. Maksimum izin verilen 3 gönderimdir.",
      submitError: "Gönderim başarısız oldu. Lütfen tekrar deneyin.",
      numberOfAttendantsLabel: "Katılımcı Sayısı",
      numberOfAttendantsPlaceholder: "Kaç kişi getiriyorsunuz?",
      isFamilyMemberLabel: "Bir şehidin, tutuklunun veya kayıp kişinin aile üyesi misiniz?",
      familyMemberTypeLabel: "Durum nedir?",
      familyMemberTypePlaceholder: "Durumu seçin",
      fatherBranchLabel: "Babanızın branşı neydi?",
      fatherBranchPlaceholder: "Babanızın branşını seçin",
      martyrTimingLabel: "Eğer şehitse, Rad' al-'udwān savaşından önce mi yoksa sonra mı?",
      martyrTimingPlaceholder: "Zamanlamayı seçin",
      isOver18Label: "18 yaşını tamamladığımı onaylıyorum",
      isUnder18Label: "18 yaşından küçük olduğumu onaylıyorum",
      ageGroupError: "Yaş grubunuzu seçmelisiniz",
      acceptedTermsLabel: "Girdiğim bilgilerin doğruluğunu teyit ediyor, sadece talep edilen amaç doğrultusunda KVKK (Kişisel Verilerin Korunması Kanunu) kapsamında işlenmesini ve paylaşılmasını kabul ediyorum",
      acceptedTermsError: "Şartları ve koşulları kabul etmelisiniz",
      arabicNameError: "Arapça karakterler kullanılamaz. Lütfen sadece İngilizce veya Türkçe harfler kullanın",
      formClosedMessage: "Salon kapasitesini aşan büyük bir katılım nedeniyle kayıt formunu kapatmamızdan dolayı özür dileriz"
    }
  };

  const fatherBranchLabels = {
    ar: {
      security_forces: 'القوات الأمنية',
      army: 'الجيش',
      civil_defense: 'الدفاع المدني'
    },
    en: {
      security_forces: 'Security Forces',
      army: 'Army',
      civil_defense: 'Civil Defense'
    },
    tr: {
      security_forces: 'Güvenlik Kuvvetleri',
      army: 'Ordu',
      civil_defense: 'Sivil Savunma'
    }
  };

  const martyrTimingLabels = {
    ar: {
      before: 'قبل رد العدوان',
      after: 'بعد رد العدوان'
    },
    en: {
      before: 'Before Rad\' al-\'udwān',
      after: 'After Rad\' al-\'udwān'
    },
    tr: {
      before: 'Rad\' al-\'udwān\'dan Önce',
      after: 'Rad\' al-\'udwān\'dan Sonra'
    }
  };

  const familyMemberTypeLabels = {
    ar: {
      martyr: 'شهيد',
      detained: 'معتقل',
      absent: 'مُغَيَّب'
    },
    en: {
      martyr: 'Martyr',
      detained: 'Detained',
      absent: 'Absent'
    },
    tr: {
      martyr: 'Şehit',
      detained: 'Tutuklu',
      absent: 'Kayıp'
    }
  };

  const getFatherBranchOptions = () => {
    const labels = fatherBranchLabels[currentLanguage] || fatherBranchLabels.en;
    return [
      { value: 'security_forces', label: labels.security_forces },
      { value: 'army', label: labels.army },
      { value: 'civil_defense', label: labels.civil_defense }
    ];
  };

  const getMartyrTimingOptions = () => {
    const labels = martyrTimingLabels[currentLanguage] || martyrTimingLabels.en;
    return [
      { value: 'before', label: labels.before },
      { value: 'after', label: labels.after }
    ];
  };

  const getFamilyMemberTypeOptions = () => {
    const labels = familyMemberTypeLabels[currentLanguage] || familyMemberTypeLabels.en;
    return [
      { value: 'martyr', label: labels.martyr },
      { value: 'detained', label: labels.detained },
      { value: 'absent', label: labels.absent }
    ];
  };

  // Function to check if string contains Arabic characters
  const containsArabic = (text) => {
    // Arabic Unicode range: \u0600-\u06FF
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text);
  };

  const content = formContent?.[currentLanguage];

  const handleDismissSuccess = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      isOver18: false,
      isUnder18: false,
      acceptedTerms: false
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field, value) => {
    // Special validation for name field
    if (field === 'name') {
      if (containsArabic(value)) {
        setErrors(prev => ({ ...prev, name: content?.arabicNameError || 'Arabic characters are not allowed' }));
        return; // Don't update the value if it contains Arabic
      }
    }
    
    // Handle mutually exclusive age checkboxes
    if (field === 'isOver18' && value === true) {
      setFormData(prev => ({ ...prev, isOver18: true, isUnder18: false }));
      if (errors?.ageGroup) {
        setErrors(prev => ({ ...prev, ageGroup: '' }));
      }
      return;
    }
    
    if (field === 'isUnder18' && value === true) {
      setFormData(prev => ({ ...prev, isUnder18: true, isOver18: false }));
      if (errors?.ageGroup) {
        setErrors(prev => ({ ...prev, ageGroup: '' }));
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = content?.requiredError;
    }

    if (!formData?.email?.trim()) {
      newErrors.email = content?.requiredError;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = content?.emailError;
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = content?.requiredError;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = content?.phoneError;
    }

    if (!formData?.isOver18 && !formData?.isUnder18) {
      newErrors.ageGroup = content?.ageGroupError;
    }

    if (!formData?.acceptedTerms) {
      newErrors.acceptedTerms = content?.acceptedTermsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Helper function to convert English keys to Arabic labels
  const getArabicLabel = (key, labelType) => {
    if (!key) return '';

    if (labelType === 'familyMemberType') {
      return familyMemberTypeLabels.ar[key] || key;
    } else if (labelType === 'fatherBranch') {
      return fatherBranchLabels.ar[key] || key;
    } else if (labelType === 'martyrTiming') {
      return martyrTimingLabels.ar[key] || key;
    }
    return key;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      // Get API token from environment variable
      const apiToken = import.meta.env.VITE_API_SECRET_TOKEN || '';

      // Prepare submission data
      const submissionData = {
        formType: 'guestbook',
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        isOver18: formData.isOver18 || false,
        isUnder18: formData.isUnder18 || false,
        acceptedTerms: formData.acceptedTerms || false,
        token: apiToken, // Include API token for authentication
        origin: window.location.origin // Include origin for CORS validation
      };

      // Get Google Script URL from environment variable
      const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';

      if (!scriptUrl) {
        throw new Error('Google Script URL not configured');
      }

      // Send to Google Apps Script
      // CORS is required - no fallback to no-cors mode
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      // Note: no-cors mode means we can't read the response
      // We'll assume success if no error was thrown

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after 10 seconds
      timeoutRef.current = setTimeout(() => {
        handleDismissSuccess();
      }, 10000);

    } catch (error) {
      console.error('Failed to submit:', error);
      setIsSubmitting(false);

      const errorMessage = error.message ||
        content?.submitError ||
        'Failed to submit. Please try again.';

      // Check if it's a phone limit error
      if (error.message && (error.message.includes('phone number') || error.message.includes('limit exceeded') || error.message.includes('already been used'))) {
        setErrors({
          phone: content?.phoneLimitError || errorMessage
        });
      } else {
        setErrors({
          submit: errorMessage
        });
      }
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Check" size={32} className="text-success" />
        </div>
        <h3 className="font-body text-xl font-semibold text-primary mb-2">
          {content?.successTitle}
        </h3>
        <p className="font-body text-text-secondary mb-6">
          {content?.successMessage}
        </p>
        <Button
          type="button"
          variant="default"
          size="lg"
          onClick={handleDismissSuccess}
        >
          {content?.okButton}
        </Button>
      </motion.div>
    );
  }

  return (
    <div>
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertCircle" size={32} className="text-text-secondary" />
        </div>
        <p className="font-body text-lg text-text-primary">
          {content?.formClosedMessage}
        </p>
      </div>
    </div>
  );
};

export default GuestBookForm;